/**
 * Single-fetch cache of the GIS Services WFS + WMS layer lists, loaded by the root layout so the
 * launcher can gate GIS Services availability; also feeds its catalog. Lifecycle mirrors
 * `operationalLayersStore`.
 */

import type { TServicesWfsLayer, TServicesWmsLayer } from '@safesystem/geoserver-api';
import { geoServer } from '#lib/config.js';
import { apiKeyStore } from '#lib/stores/apiKeyStore.svelte.js';
import { FetchStatus } from '#lib/types.js';
import { getLogger } from '@logtape/logtape';

const log = getLogger(['platform', 'gis-services', 'layers-store']);

function createGisServicesLayersStore() {
	let wfsLayers = $state<TServicesWfsLayer[]>([]);
	let wmsLayers = $state<TServicesWmsLayer[]>([]);
	let status = $state<FetchStatus>(FetchStatus.IDLE);
	let error = $state<string | null>(null);
	let loadedKey: string | null = null;
	let inFlight: Promise<void> | null = null;

	// Undefined until the lists resolve, so availability falls back to the static config meanwhile.
	const available = $derived(
		status === FetchStatus.SUCCESS || status === FetchStatus.FAILURE
			? wfsLayers.length + wmsLayers.length > 0
			: undefined
	);

	return {
		get wfsLayers() {
			return wfsLayers;
		},
		get wmsLayers() {
			return wmsLayers;
		},
		get status() {
			return status;
		},
		get error() {
			return error;
		},
		get available() {
			return available;
		},

		/**
		 * Fetch the WFS + WMS services layer lists for `apiKey`. Idempotent for the
		 * same key once successful; in-flight calls share a single promise.
		 */
		async load(apiKey: string): Promise<void> {
			if (status === FetchStatus.SUCCESS && loadedKey === apiKey) return;
			if (inFlight && loadedKey === apiKey) return inFlight;

			const cred = apiKeyStore.credentials;
			if (!cred) {
				log.error('load: no credentials — validate the api key first');
				return;
			}

			loadedKey = apiKey;
			status = FetchStatus.LOADING;
			error = null;

			inFlight = (async () => {
				try {
					const [wfs, wms] = await Promise.all([
						geoServer.getServicesWfsLayers(apiKey, cred.namespace),
						geoServer.getServicesWmsLayers(apiKey, cred.namespace)
					]);
					// Discard a stale response if the key changed mid-flight.
					if (loadedKey !== apiKey) return;
					wfsLayers = wfs ?? [];
					wmsLayers = wms ?? [];
					status = FetchStatus.SUCCESS;
					log.info('Loaded {wfsCount} WFS and {wmsCount} WMS service layers', {
						wfsCount: wfsLayers.length,
						wmsCount: wmsLayers.length
					});
				} catch (err) {
					if (loadedKey !== apiKey) return;
					wfsLayers = [];
					wmsLayers = [];
					status = FetchStatus.FAILURE;
					error = err instanceof Error ? err.message : String(err);
					log.error('Load error: {error}', { error });
				} finally {
					inFlight = null;
				}
			})();

			return inFlight;
		},

		/** Reset state — call when the api key is cleared. */
		clear() {
			wfsLayers = [];
			wmsLayers = [];
			status = FetchStatus.IDLE;
			error = null;
			loadedKey = null;
			inFlight = null;
		}
	};
}

export const gisServicesLayersStore = createGisServicesLayersStore();
